/**
 * `aria-disabled` rather than `disabled` on a blocked primary action, so the button stays
 * focusable and a keyboard user can reach it and read the block explaining why it is inert. The
 * component short-circuits `onClick` itself, so no caller can forget the guard (§6.3).
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import styles from './Button.module.css';

export type ButtonVariant = 'default' | 'ghost' | 'primary' | 'danger' | 'dangerSolid';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
  /** Not valid right now, without removing the control from the tab order. */
  inactive?: boolean;
  /** A write in flight. Announced, and inert for the same reason as `inactive`. */
  pending?: boolean;
  icon?: ReactNode;
}

const variantClass: Record<ButtonVariant, string | undefined> = {
  default: undefined,
  ghost: styles.ghost,
  primary: styles.primary,
  danger: styles.danger,
  dangerSolid: styles.dangerSolid,
};

export function Button({
  variant = 'default',
  size = 'md',
  inactive = false,
  pending = false,
  icon,
  children,
  className,
  onClick,
  type = 'button',
  ...rest
}: ButtonProps) {
  const blocked = inactive || pending;
  const classes = [
    styles.btn,
    variantClass[variant],
    size === 'sm' ? styles.sm : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      {...rest}
      type={type}
      className={classes}
      aria-disabled={blocked || undefined}
      aria-busy={pending || undefined}
      onClick={(event) => {
        if (blocked) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
    >
      {pending ? <span className={styles.spinner} aria-hidden="true" /> : icon}
      {children}
    </button>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: ReactNode;
  /** Required: the button has no visible text, and it describes the action, not the state. */
  'aria-label': string;
}

export function IconButton({ icon, className, ...rest }: IconButtonProps) {
  return (
    <button
      {...rest}
      type={rest.type ?? 'button'}
      className={[styles.iconbtn, className].filter(Boolean).join(' ')}
    >
      {icon}
    </button>
  );
}
